import { useEffect, useState } from "react";
import { Buildings, CheckCircle, Plus, SpinnerGap, Trash } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "@/components/ui/sonner";

export default function AdminBuildings() {
  const [buildings, setBuildings] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", copy_from: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/buildings").then((r) => setBuildings(r.data)).catch(() => setBuildings([]));
  useEffect(() => {
    load();
  }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Le nom de la copropriété est requis");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/buildings", { ...form, copy_from: form.copy_from || null });
      setForm({ name: "", address: "", copy_from: "" });
      await load();
      const copied = [
        data.copied_services ? `${data.copied_services} prestataire(s)` : "",
        data.copied_contacts ? `${data.copied_contacts} contact(s)` : "",
      ].filter(Boolean).join(" et ");
      toast.success(copied ? `Copropriété ajoutée — ${copied} copié(s)` : "Copropriété ajoutée");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  const activate = async (b) => {
    try {
      await api.post(`/admin/buildings/${b.id}/activate`);
      toast.success(`« ${b.name} » est maintenant la copropriété active`);
      window.setTimeout(() => window.location.reload(), 700);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (b) => {
    try {
      await api.delete(`/admin/buildings/${b.id}`);
      await load();
      toast.success(`« ${b.name} » supprimée`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-black/10 px-4 py-3 text-sm outline-none transition-colors focus:border-primary";

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_1.2fr]" data-testid="admin-buildings-tab">
      <form onSubmit={add} className="h-fit rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <h2 className="font-display text-lg font-bold tracking-tight">Ajouter une copropriété</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Chaque copropriété a ses propres signalements, passages, contacts, prestataires et documents. Activez celle que le portail doit afficher.
        </p>
        <div className="mt-5 space-y-3">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom (ex. Résidence Les Pins)" data-testid="building-name-input" className={inputCls} />
          <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Adresse complète" data-testid="building-address-input" className={inputCls} />
          <div>
            <label htmlFor="building-copy-from" className="mb-1 block text-[11px] font-bold text-muted-foreground">
              Copier depuis une copropriété existante (optionnel)
            </label>
            <select
              id="building-copy-from"
              value={form.copy_from}
              onChange={(e) => setForm({ ...form, copy_from: e.target.value })}
              data-testid="building-copy-from-select"
              className={inputCls}
            >
              <option value="">Ne rien copier</option>
              {(buildings || []).map((b) => (
                <option key={b.id} value={b.id} data-testid={`copy-from-${b.id}`}>
                  {b.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
              Copie les prestataires (sans leurs notes), les contacts (gardien, conseil syndical…), les rubriques (ordre, masquage, libellés, consignes), les immeubles, le message d'accueil et les options d'affichage.
            </p>
          </div>
        </div>
        <button
          type="submit"
          disabled={saving}
          data-testid="building-add-button"
          className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground transition-colors hover:bg-[#265442] disabled:opacity-60"
        >
          {saving ? <SpinnerGap size={16} className="animate-spin" /> : <Plus size={16} weight="bold" />}
          Ajouter la copropriété
        </button>
      </form>

      <div className="space-y-3" data-testid="admin-buildings-list">
        {buildings === null && <div className="h-24 animate-pulse rounded-3xl bg-black/5" />}
        {buildings?.map((b) => (
          <div
            key={b.id}
            className={cn(
              "flex items-center gap-4 rounded-3xl border bg-white p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]",
              b.active ? "border-primary ring-1 ring-primary/20" : "border-black/5"
            )}
            data-testid={`building-row-${b.id}`}
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Buildings size={20} weight="duotone" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold">
                {b.name}
                {b.active && (
                  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#E2F6ED] px-2.5 py-0.5 text-[10px] font-bold text-[#007A4D]">
                    <CheckCircle size={11} weight="fill" /> Active
                  </span>
                )}
              </p>
              <p className="truncate text-xs text-muted-foreground">{b.address || "—"}</p>
            </div>
            {!b.active && (
              <>
                <button
                  onClick={() => activate(b)}
                  data-testid={`building-activate-${b.id}`}
                  className="shrink-0 rounded-full bg-primary px-4 py-2 text-xs font-bold text-primary-foreground transition-colors hover:bg-[#265442]"
                >
                  Activer
                </button>
                <button
                  onClick={() => remove(b)}
                  data-testid={`building-delete-${b.id}`}
                  aria-label={`Supprimer ${b.name}`}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-black/10 text-foreground/50 transition-colors hover:border-[#C90A0A] hover:text-[#C90A0A]"
                >
                  <Trash size={16} weight="duotone" />
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
